import React, { FC } from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubcontractMessageField } from 'models/client-directory';
import { LOCALE_LANGUAGE_OPTIONS, LocaleLanguage } from 'models/locale-format';
import InfoText from 'components/shared/info-text.component';
import { MESSAGE_FIELD_OPTIONS } from 'components/order-flow/subcontract-order/communication-step/subcontract-message';

type SubcontractMessageDefaultsProps = {
    fields: SubcontractMessageField[];
    language: LocaleLanguage | null;
    onFieldsChange: (fields: SubcontractMessageField[]) => void;
    onLanguageChange: (language: LocaleLanguage | null) => void;
};

/** Select value for "no preferred language" (MUI Select cannot hold null). */
const SENDER_LANGUAGE = '';

/**
 * DD-3689: what the message to this supplier is built from, and the language it is written
 * in. Both only pre-fill the subcontract flow's communication step.
 */
const SubcontractMessageDefaults: FC<SubcontractMessageDefaultsProps> = ({
    fields,
    language,
    onFieldsChange,
    onLanguageChange,
}) => {
    const { t: translate } = useTranslation(['orders', 'messages']);

    const toggle = (field: SubcontractMessageField, checked: boolean) =>
        onFieldsChange(
            checked ? [...fields.filter((selected) => selected !== field), field] : fields.filter((f) => f !== field),
        );

    return (
        <Box mb={2}>
            <Typography variant="h3" mb={1}>
                {translate('orders:SUBCONTRACT_MESSAGE_DEFAULTS')}
            </Typography>
            <InfoText text={translate('orders:SUBCONTRACT_MESSAGE_DEFAULTS_INFO')} />
            <Box display="flex" flexDirection="column" my={1}>
                {MESSAGE_FIELD_OPTIONS.map((option) => (
                    <FormControlLabel
                        key={option.field}
                        control={
                            <Checkbox
                                checked={fields.includes(option.field)}
                                onChange={(_event, checked) => toggle(option.field, checked)}
                                data-test={`subcontract-defaults.message-field.${option.field}`}
                            />
                        }
                        label={translate(option.title)}
                    />
                ))}
            </Box>
            <FormControl sx={{ minWidth: 240 }} size="small">
                <InputLabel id="subcontract-message-language" shrink>
                    {translate('orders:SUBCONTRACT_MESSAGE_LANGUAGE')}
                </InputLabel>
                <Select
                    labelId="subcontract-message-language"
                    label={translate('orders:SUBCONTRACT_MESSAGE_LANGUAGE')}
                    value={language ?? SENDER_LANGUAGE}
                    displayEmpty
                    notched
                    onChange={(event) => onLanguageChange((event.target.value as LocaleLanguage) || null)}
                    data-test="subcontract-defaults.message-language"
                >
                    <MenuItem value={SENDER_LANGUAGE}>{translate('orders:SUBCONTRACT_MESSAGE_LANGUAGE_OWN')}</MenuItem>
                    {LOCALE_LANGUAGE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export default SubcontractMessageDefaults;
